import React, { useEffect, useState, VoidFunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardListItem,
  CardListItemProps
} from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemIconButton } from 'record-it-components/cardList/CardListItemIconButton/CardListItemIconButton';
import { CardListItemTitle } from 'record-it-components/cardList/CardListItemTitle/CardListItemTitle';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ClockTick } from 'record-it-utils/utils/ClockTick/ClockTick';
import { DurationUtils } from 'record-it-utils/utils/DurationUtils/DurationUtils';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { ImmutableStopwatchInfo } from '../../../classes/timeTracking/ImmutableStopwatch/ImmutableStopwatch';
import { StopwatchWorkDayEvaluator } from '../../../classes/timeTracking/StopwatchWorkDayEvaluator/StopwatchWorkDayEvaluator';
import { useStopwatchManager } from '../../../hooks/stopwatch/useStopwatchManager';
import { useAppDispatch } from '../../../store/hooks';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';

export type StopwatchListItemProps = PropsWithoutChildren<CardListItemProps> & {
  stopwatchInfo: ImmutableStopwatchInfo;
};

export const StopwatchListItem: VoidFunctionComponent<StopwatchListItemProps> =
  React.memo((props) => {
    const { stopwatchInfo, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });
    const [renderedDuration, setRenderedDuration] = useState(
      stopwatchInfo.getDuration()
    );

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
      setRenderedDuration(stopwatchInfo.getDuration());

      const disposable = ClockTick.onSecondTick(() => {
        setRenderedDuration(stopwatchInfo.getDuration());
      });

      return () => disposable.dispose();
    }, [stopwatchInfo, setRenderedDuration]);

    const manager = useStopwatchManager();

    const handleDeleteClick = useEventCallback(() => {
      manager.remove(stopwatchInfo.getId());
    });

    const handlePauseClick = useEventCallback(() => {
      manager.pause(stopwatchInfo.getId());
    });

    const handleStartClick = useEventCallback(() => {
      manager.start(stopwatchInfo.getId());
    });

    const handleCreateClick = useEventCallback(() => {
      const evaluator = new StopwatchWorkDayEvaluator();
      const data = evaluator.evaluate(stopwatchInfo);

      dispatch(
        createWorkTimeEntrySlice.actions.startCreation({
          startDate: data.startDate.toISOString(),
          endDate: data.endDate.toISOString(),
          breaks: data.breaks,
          note: '',
          userGroupId: stopwatchInfo.getUserGroupId(),
          stopwatchId: stopwatchInfo.getId()
        })
      );

      navigate(RoutePaths.createWorkTimeEntry());
    });

    return (
      <CardListItem {...remainingProps}>
        <CardListItemTitle>
          {DurationUtils.formatDurationToHourMinuteSecondString(
            renderedDuration
          )}
        </CardListItemTitle>

        {stopwatchInfo.isStarted() ? (
          <CardListItemIconButton
            {...iconConfigurations.prefixed.stopwatch.pause}
            onClick={handlePauseClick}
            data-testid="StopwatchListItem.PauseButton"
          ></CardListItemIconButton>
        ) : (
          <CardListItemIconButton
            {...iconConfigurations.prefixed.stopwatch.play}
            onClick={handleStartClick}
            data-testid="StopwatchListItem.PlayButton"
          ></CardListItemIconButton>
        )}
        <CardListItemIconButton
          {...iconConfigurations.prefixed.listItem.deleteIcon}
          onClick={handleDeleteClick}
          data-testid="StopwatchListItem.DeleteButton"
        ></CardListItemIconButton>
        <CardListItemIconButton
          {...iconConfigurations.prefixed.listItem.createFromListItem}
          onClick={handleCreateClick}
          data-testid="StopwatchListItem.CreateButton"
        ></CardListItemIconButton>
      </CardListItem>
    );
  });
